import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, ListChecks, AlertTriangle, LogIn, Eye, CheckCircle2, DollarSign, Zap,
} from "lucide-react";
import { sr, actAs, auth } from "../../lib/sr";
import HealthBadge from "./HealthBadge";

const money = (n) => "$" + Number(n || 0).toLocaleString();

export default function AdminQueue() {
  const nav = useNavigate();
  const me = auth.getUser();
  const [data, setData] = useState(null);
  const [staff, setStaff] = useState([]);
  const [mine, setMine] = useState(false);
  const [saving, setSaving] = useState(null);

  const load = (m) => sr.adminAssistedQueue(m).then(setData).catch(() => setData({ error: true }));
  useEffect(() => { load(mine); }, [mine]);
  useEffect(() => { sr.adminStaff().then((r) => setStaff(r.staff || [])).catch(() => {}); }, []);

  const assign = async (clientId, managerId) => {
    setSaving(clientId);
    try { await sr.adminAssignManager(clientId, managerId); toast.success("Manager assigned"); load(mine); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not assign"); }
    finally { setSaving(null); }
  };

  const act = async (c) => {
    actAs.set(c.id, c.name);
    try { await sr.adminActAs(c.id); } catch { /* best-effort */ }
    toast.success(`Now managing ${c.name}`);
    window.location.assign("/app");
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load the queue. Admin access is required.</p></div>;

  const queue = data.queue || [];

  return (
    <div data-testid="admin-queue-page">
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="sr-badge" style={{ marginBottom: 8 }}><ShieldCheck size={12} /> Admin · Assisted Queue</span>
          <h1 style={{ fontSize: 30 }}>Assisted work queue</h1>
          <p className="sr-muted" style={{ marginTop: 6 }}>Team-run clients that still need attention — most urgent first.</p>
        </div>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setMine((v) => !v)} data-testid="queue-mine-toggle">
          {mine ? "Showing: My clients" : "Showing: All"}
        </button>
      </div>

      {queue.length === 0 ? (
        <div className="sr-card" data-testid="admin-queue-empty">
          <p style={{ display: "flex", alignItems: "center", gap: 8, color: "#067647", fontWeight: 700 }}>
            <CheckCircle2 size={16} /> All caught up
          </p>
          <p className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>
            {mine ? "None of your assisted clients need attention right now." : "No assisted clients need attention right now."}
          </p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {queue.map((c) => (
            <div key={c.id} className="sr-card" data-testid={`queue-item-${c.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <Zap size={15} style={{ color: "#E0A93B" }} />
                    <strong style={{ color: "var(--navy)", fontSize: 16 }}>{c.name}</strong>
                    <HealthBadge health={c.health} size="sm" />
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                    {c.reasons.map((r, i) => (
                      <span key={i} className="sr-badge sr-badge-amber" data-testid={`reason-${c.id}-${i}`}>
                        <AlertTriangle size={10} /> {r}
                      </span>
                    ))}
                  </div>
                  <div className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>
                    <CheckCircle2 size={11} /> {c.live_campaigns} live · <DollarSign size={11} /> {money(c.monthly_budget)}/mo ·
                    manager: {c.assigned_manager_name || "none"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <select className="sr-select" value={c.assigned_manager_id || ""} disabled={saving === c.id}
                    onChange={(e) => assign(c.id, e.target.value)} data-testid={`queue-assign-${c.id}`}
                    style={{ padding: "6px 8px", fontSize: 12, maxWidth: 170 }}>
                    <option value="">Assign manager…</option>
                    {staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
                  </select>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => nav(`/app/admin/clients/${c.id}`)} data-testid={`queue-view-${c.id}`}>
                    <Eye size={13} /> View
                  </button>
                  <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => act(c)} data-testid={`queue-act-${c.id}`}>
                    <LogIn size={13} /> Act as
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
